import { useEffect, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { useModalEffects } from '@/shared/hooks/use-modal-effects';

import type { PatientListItem } from '@/api/adapters/patients';
import { maskNameKeepLast } from '@/pages/medical/screens/home/utils';
import {
	CLOSE_REASONS,
	createClosePatientSchema,
	type ClosePatientFormValues,
} from '@/features/patients/schemas/close-patient.schema';

interface Props {
	open: boolean;
	patient: PatientListItem;
	isSubmitting?: boolean;
	onClose: () => void;
	onSubmit: (values: ClosePatientFormValues) => void;
}

export function ClosePatientModal({
	open,
	patient,
	isSubmitting = false,
	onClose,
	onSubmit,
}: Props) {
	const { t } = useTranslation(['medical', 'common', 'validation']);

	const schema = useMemo(
		() => createClosePatientSchema(patient.dischargeDate),
		[patient.dischargeDate],
	);

	const defaultValues: ClosePatientFormValues = {
		reason: 'COMPLETED' as const,
		endDate: '',
		memo: '',
	};

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<ClosePatientFormValues>({
		resolver: zodResolver(schema),
		mode: 'onTouched',
		defaultValues: {
			...defaultValues,
			endDate: new Date().toISOString().split('T')[0],
		},
	});

	const modalRef = useRef<HTMLDivElement>(null);

	useModalEffects({
		open,
		onClose,
		modalRef,
	});

	// Reset form when modal opens
	useEffect(() => {
		if (open) {
			reset({
				...defaultValues,
				endDate: new Date().toISOString().split('T')[0],
			});
		}
	}, [open, reset]);

	const handleFormSubmit = (values: ClosePatientFormValues) => {
		onSubmit(values);
	};

	if (!open) return null;

	const shortMrn = patient.mrn?.startsWith('2026')
		? 'M' + patient.mrn.slice(4)
		: patient.mrn;

	return (
		<div
			ref={modalRef}
			className='modal-overlay modal-overlay--visible'
			role='dialog'
			aria-modal='true'
		>
			<div className='modal modal--lg'>
				{/* ── Header ── */}
				<div className='modal__header'>
					<h3 className='modal__title'>
						{t('medical:patients.header.closeModal.title')}
					</h3>
					<button
						type='button'
						className='modal__close'
						onClick={onClose}
						aria-label={t('common:actions.close')}
					>
						<X size={20} />
					</button>
				</div>

				{/* ── Body ── */}
				<form onSubmit={handleSubmit(handleFormSubmit)}>
					<div className='modal__body'>
						{/* Patient info description */}
						<p className='modal__desc text-sm text-gray-600 mb-4'>
							<Trans
								ns='medical'
								i18nKey='patients.header.closeModal.desc'
								values={{ name: maskNameKeepLast(patient.name), mrn: shortMrn }}
								components={{ strong: <strong className='text-gray-900' /> }}
							/>
						</p>

						{/* Warning banner */}
						<div className='modal__warning mb-[18px] px-3.5 py-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 leading-relaxed'>
							<Trans
								ns='medical'
								i18nKey='patients.header.closeModal.warning'
								components={{ strong: <strong /> }}
							/>
						</div>

						{/* Close reason */}
						<div className='form-field mb-5'>
							<label className='text-sm font-medium text-gray-700 mb-2 block'>
								{t('medical:patients.header.closeModal.reasons.label')}{' '}
								<span className='text-red-500'>*</span>
							</label>
							<div className='grid grid-cols-2 gap-2'>
								{CLOSE_REASONS.map((r) => (
									<label
										key={r}
										className='close-reason-option flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 transition-colors has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50'
									>
										<input
											type='radio'
											value={r}
											{...register('reason')}
											className='accent-blue-600'
										/>
										<div className='close-reason-card'>
											<strong className='block text-sm text-gray-800'>
												{t(
													`medical:patients.header.closeModal.reasons.${r}.title`,
												)}
											</strong>
											<small className='block text-xs text-gray-500 mt-0.5'>
												{t(
													`medical:patients.header.closeModal.reasons.${r}.sub`,
												)}
											</small>
										</div>
									</label>
								))}
							</div>
						</div>

						{/* End date */}
						<div className='form-field mt-3.5 mb-5'>
							<label className='block text-sm font-medium text-gray-700 mb-1.5'>
								{t('medical:patients.header.closeModal.endDate')}{' '}
								<span className='text-red-500'>*</span>
							</label>
							<div className='w-1/2'>
								<DatePicker register={register('endDate')} />
							</div>
							{errors.endDate && (
								<span className='text-xs text-red-500 mt-1'>
									{t(String(errors.endDate.message))}
								</span>
							)}
						</div>

						{/* Memo */}
						<div className='form-field mt-3.5 mb-2'>
							<label className='block text-sm font-medium text-gray-700 mb-1.5'>
								{t('medical:patients.header.closeModal.memo')}{' '}
								<span className='text-xs text-gray-400 font-normal'>
									{t('medical:patients.header.closeModal.memoHint')}
								</span>
							</label>
							<textarea
								className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400'
								rows={3}
								{...register('memo')}
								placeholder={t(
									'medical:patients.header.closeModal.memoPlaceholder',
								)}
							/>
							{errors.memo && (
								<span className='text-xs text-red-500 mt-1'>
									{t(String(errors.memo.message))}
								</span>
							)}
						</div>
					</div>

					{/* ── Footer ── */}
					<div className='modal__footer'>
						<Button type='button' variant='secondary' onClick={onClose}>
							{t('medical:patients.header.closeModal.cancel')}
						</Button>
						<button
							type='submit'
							className='btn btn-default bg-red-500 hover:bg-red-600 text-white border-red-500 hover:border-red-600'
							disabled={isSubmitting}
						>
							{t('medical:patients.header.closeModal.confirm')}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
